import { create } from 'zustand';
import type { EquippedSlot } from '../api/wardrobe';

/**
 * The wardrobe's local, single-level-undo edit buffer (FABLE5_BUILD_PROMPT.md §11.4,
 * task-14-brief.md). Exact shape is load-bearing for Task 15 (dye sheet + Save Look), so it's
 * documented here rather than only in the brief:
 *
 * - `draft` is the full equipped-slots map as the player is currently seeing it (starts as a
 *   copy of the server's `WardrobeState.equipped`, mutated locally by `equip`/`unequip`/`setDye`).
 * - `dirty` means "this draft has changes not yet saved as a Look" — it is NOT about whether the
 *   draft has been committed to the server. WardrobeScreen commits every equip/unequip/setDye to
 *   the server immediately (optimistic `usePutOutfit`), so `dirty` stays true across any number
 *   of already-committed changes until Task 15's Save Look flow (or `reset`) clears it. The one
 *   exception is `undo`: reviewer finding M6 (Task 14 fix round) — undoing the only change back
 *   to exactly the state `reset` last established is, by definition, "no unsaved changes"
 *   again, so `undo` clears `dirty` when the post-undo draft equals `resetBaseline`, and leaves
 *   it `true` otherwise (e.g. two changes made, only the most recent undone).
 * - `lastChange` holds only the most recent mutation's slot + its prior value, enabling exactly
 *   one level of undo (§11.4: "a single-level undo on every change"). `undo` restores that one
 *   slot and clears `lastChange`, so a second `undo` is a no-op.
 * - `clearDye` (Task 15, additive — not in task-14-brief.md's original interface list) is
 *   `setDye`'s inverse: removes one channel from an equipped item's dye map entirely (the dye
 *   sheet's "no dye" option per channel) rather than setting it to some sentinel value. Same
 *   dirty/lastChange bookkeeping as `setDye`.
 * - `resetBaseline` is an internal-only field (not part of the brief's documented public shape,
 *   purely additive) recording the equipped map as of the last `reset` call, used only for the
 *   `undo`-clears-`dirty` comparison above.
 * - There is deliberately no `commit()` here — a vanilla store shouldn't own a react-query
 *   mutation hook. WardrobeScreen owns committing: it calls `equip`/`unequip`/`undo` to update
 *   this store, reads the resulting `draft` back out, and fires `usePutOutfit(companionId)` with
 *   it — rolling this store back via `reset(serverStateAfterRollback)` if the server rejects it.
 *
 * Module-level `create()` (same pattern as `shell/scrollStore.ts`) rather than one instance per
 * screen: the store must survive exactly as long as WardrobeScreen does and no longer, so the
 * screen calls `reset(wardrobe.data.equipped)` once per companion on mount/companion-change
 * (never on every wardrobe refetch — see WardrobeScreen's own comment) to avoid a stale draft
 * leaking from a previously visited companion's wardrobe into this one.
 */
export type LastChange = { slot: string; previous: EquippedSlot | null };

export type OutfitDraftState = {
  draft: Record<string, EquippedSlot>;
  dirty: boolean;
  lastChange: LastChange | null;
  /** Internal only — see header comment. Not part of the brief's documented public shape. */
  resetBaseline: Record<string, EquippedSlot>;
  equip: (slot: string, itemCode: string) => void;
  unequip: (slot: string) => void;
  setDye: (slot: string, channel: string, dyeCode: string) => void;
  /** Removes one channel from a slot's dye map (Task 15's dye sheet "no dye" option) — see
   *  header comment. Defensive no-op if the slot isn't equipped or the channel isn't set. */
  clearDye: (slot: string, channel: string) => void;
  undo: () => void;
  reset: (fromServer: Record<string, EquippedSlot>) => void;
};

/**
 * Deep-equality for two equipped-slot maps (itemCode + dye channels, order-independent) — used
 * both internally (`undo`'s dirty recomputation) and externally by `WardrobeScreen` to decide
 * whether the draft has diverged from the server's fresh truth and needs re-syncing (review
 * finding C2). Exported from here, not duplicated, so both call sites agree on what "the same
 * outfit" means.
 */
export function equippedMapsEqual(
  a: Record<string, EquippedSlot>,
  b: Record<string, EquippedSlot>,
): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((slot) => {
    const av = a[slot];
    const bv = b[slot];
    if (!bv || av.itemCode !== bv.itemCode) return false;
    const aDyeKeys = Object.keys(av.dye);
    if (aDyeKeys.length !== Object.keys(bv.dye).length) return false;
    return aDyeKeys.every((ch) => av.dye[ch] === bv.dye[ch]);
  });
}

export const useOutfitDraft = create<OutfitDraftState>((set, get) => ({
  draft: {},
  dirty: false,
  lastChange: null,
  resetBaseline: {},

  equip: (slot, itemCode) => set((s) => ({
    draft: { ...s.draft, [slot]: { itemCode, dye: {} } },
    dirty: true,
    lastChange: { slot, previous: s.draft[slot] ?? null },
  })),

  unequip: (slot) => set((s) => {
    if (!(slot in s.draft)) return s; // defensive: nothing to remove
    const draft = { ...s.draft };
    const previous = draft[slot];
    delete draft[slot];
    return { draft, dirty: true, lastChange: { slot, previous } };
  }),

  setDye: (slot, channel, dyeCode) => set((s) => {
    const current = s.draft[slot];
    if (!current) return s; // defensive: can't dye an item that isn't equipped
    return {
      draft: { ...s.draft, [slot]: { ...current, dye: { ...current.dye, [channel]: dyeCode } } },
      dirty: true,
      lastChange: { slot, previous: current },
    };
  }),

  clearDye: (slot, channel) => set((s) => {
    const current = s.draft[slot];
    if (!current || !(channel in current.dye)) return s; // defensive: nothing to clear
    const dye = { ...current.dye };
    delete dye[channel];
    return {
      draft: { ...s.draft, [slot]: { ...current, dye } },
      dirty: true,
      lastChange: { slot, previous: current },
    };
  }),

  undo: () => {
    const { lastChange } = get();
    if (!lastChange) return; // single-level undo: nothing left to revert
    set((s) => {
      const draft = { ...s.draft };
      if (lastChange.previous) draft[lastChange.slot] = lastChange.previous;
      else delete draft[lastChange.slot];
      // M6: back to exactly the last-reset baseline (the common "undo my one change" case) is a
      // clean slate again; still diverged from baseline (e.g. a second, earlier change remains)
      // stays dirty.
      return { draft, lastChange: null, dirty: !equippedMapsEqual(draft, s.resetBaseline) };
    });
  },

  reset: (fromServer) => set({
    draft: { ...fromServer }, dirty: false, lastChange: null, resetBaseline: { ...fromServer },
  }),
}));
